import type { SampleArtwork } from "@shared/sample-artwork.ts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  Card,
  FILL_BODY,
  FILL_CARD,
  FILL_PAGE,
  PageHeader,
  PRIMARY_BTN,
  ROW_SCROLL,
  STICKY_THEAD,
} from "../components/account-ui.tsx";
import { DownloadIcon } from "../icons/index.tsx";
import { type AppConfig, api } from "../lib/api.ts";
import { isStaffRole } from "../lib/legacy.ts";

export const Route = createFileRoute("/app/$customerId/samples")({
  component: SamplesPage,
  beforeLoad: async ({ context, params }) => {
    const config = await context.queryClient.ensureQueryData({
      queryKey: ["config"],
      queryFn: () => api.get<AppConfig>("/config"),
    });
    if (!config.user || !isStaffRole(config.user.role)) {
      throw redirect({
        to: "/app/$customerId/mycampaigns",
        params: { customerId: params.customerId },
      });
    }
  },
});

function label(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function SamplesPage() {
  const data = useQuery({
    queryKey: ["samples"],
    queryFn: () => api.get<{ samples: SampleArtwork[] }>("/samples"),
  });
  const samples = data.data?.samples ?? [];

  return (
    <div className={FILL_PAGE}>
      <PageHeader
        title="Samples"
        subtitle="Print-ready artwork for every product and mail variant."
        action={
          <a className={PRIMARY_BTN} href="/api/samples/zip">
            <DownloadIcon className="h-4 w-4" />
            Download all
          </a>
        }
      />
      <Card className={FILL_CARD} bodyClass={FILL_BODY}>
        {data.isError ? (
          <p className="px-5 py-8 text-sm text-gray-600">
            {data.error instanceof Error ? data.error.message : "Could not load samples"}
          </p>
        ) : (
          <div className={ROW_SCROLL}>
            <table className="w-full text-left text-sm">
              <thead className={STICKY_THEAD}>
                <tr>
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Product</th>
                  <th className="px-5 py-3 font-semibold">Mail</th>
                  <th className="px-5 py-3 font-semibold">Speed</th>
                  <th className="px-5 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {samples.map((sample) => (
                  <tr key={sample.id}>
                    <td className="px-5 py-4 font-semibold text-gray-900">{sample.name}</td>
                    <td className="px-5 py-4 text-gray-600">{sample.product}</td>
                    <td className="px-5 py-4 text-gray-600">{label(sample.mailType)}</td>
                    <td className="px-5 py-4 text-gray-600">{label(sample.mailSpeed)}</td>
                    <td className="px-5 py-4 text-right">
                      <a
                        className="font-medium text-primary-700 hover:text-primary-800"
                        href={`/api/samples/${encodeURIComponent(sample.id)}/file`}
                      >
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
